/**
 * 施術師毎CSV出力 Page (SCR-046) — /rezepts/init_massagers.
 *
 * A2/R2 split resolved via useTheme().serviceType.
 */

import CollapsibleCard from '@/components/molecules/CollapsibleCard';
import BottomBar from '@/components/organisms/Footer';
import useAppToast from '@/hooks/useAppToast';
import { useSidebarOpen } from '@shared/providers/SidebarContext';
import dayjs from 'dayjs';
import React, { useCallback, useState } from 'react';

import {
  useMassagerCsvExport,
  useMassagerCsvMassagers,
  useMassagerCsvSearch,
} from './hooks/useMassagerCsv';

import MassagerCsvFooter from './components/MassagerCsvFooter';
import MassagerCsvSearchForm from './components/MassagerCsvSearchForm';
import MassagerCsvTable from './components/MassagerCsvTable';

import type { MassagerCsvSearchParams } from './types';
import type { MassagerCsvSearchFormValues } from './urlParams';
import { formValuesToSearchPayload } from './urlParams';

const PAGE_SIZE = 50;

const MassagerCsvPage: React.FC = () => {
  const sidebarOpen = useSidebarOpen();
  const toast = useAppToast();

  const cardTitle = '施術師毎CSV出力';

  const [searchParams, setSearchParams] = useState<MassagerCsvSearchParams | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [massagerTargetMonth, setMassagerTargetMonth] = useState<string | null>(() =>
    dayjs().format('YYYYMM')
  );

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [excludedIds, setExcludedIds] = useState<string[]>([]);
  const [outputOption1, setOutputOption1] = useState(true);

  const { massagers } = useMassagerCsvMassagers(massagerTargetMonth);
  const { rows, pagination, isLoading } = useMassagerCsvSearch(searchParams);
  const { triggerExport, isLoading: isExporting } = useMassagerCsvExport();
  const hasExportableRows = hasSearched && (pagination?.total ?? rows.length) > 0;

  const handleSearch = useCallback((values: MassagerCsvSearchFormValues) => {
    const payload = formValuesToSearchPayload(values);
    const params: MassagerCsvSearchParams = {
      ...payload,
      page: 1,
      per_page: PAGE_SIZE,
    };
    setSearchParams(params);
    setHasSearched(true);
    setSelectedIds([]); // Reset selection on new search
    setIsAllSelected(false);
    setExcludedIds([]);
  }, []);

  const handlePageChange = useCallback(
    (page: number) => {
      if (!searchParams) return;
      setSearchParams(prev => (prev ? { ...prev, page } : null));
    },
    [searchParams]
  );

  const handleExportCsv = useCallback(() => {
    if (!searchParams) return;
    if (!isAllSelected && selectedIds.length === 0) {
      toast.error('対象のレセプトを選択してください。');
      return;
    }

    triggerExport({
      rezept_id: isAllSelected ? undefined : selectedIds.join(','),
      sort_radio: searchParams.sort_radio,
      rezept_kbn: searchParams.rezept_kbn,
      output_option1: outputOption1 ? 1 : 0,
      select_all: isAllSelected ? 1 : 0,
      excluded_rezept_id: isAllSelected ? excludedIds.join(',') : undefined,
      search_visitmonth: searchParams.search_visitmonth,
      serial_number: searchParams.serial_number,
      massager_id: searchParams.massager_id,
    });
  }, [searchParams, isAllSelected, selectedIds, outputOption1, excludedIds, triggerExport, toast]);

  return (
    <div className="pb-4">
      <MassagerCsvSearchForm
        onSearch={handleSearch}
        onVisitmonthChange={setMassagerTargetMonth}
        massagers={massagers}
        isLoading={isLoading}
        cardTitle={cardTitle}
        totalCount={hasSearched ? pagination?.total : undefined}
      />

      {hasSearched && (
        <div className="mt-4">
          <CollapsibleCard>
            <MassagerCsvTable
              data={rows}
              pagination={pagination}
              onPageChange={handlePageChange}
              isLoading={isLoading}
              hasSearched={hasSearched}
              selectedIds={selectedIds}
              onSelectIds={setSelectedIds}
              isAllSelected={isAllSelected}
              excludedIds={excludedIds}
              onSelectAllChange={checked => {
                setIsAllSelected(checked);
                setSelectedIds([]);
                setExcludedIds([]);
              }}
              onExcludedIdsChange={setExcludedIds}
            />
          </CollapsibleCard>
        </div>
      )}

      {hasExportableRows && (
        <BottomBar
          variant="action"
          fixed
          className={sidebarOpen ? 'left-60' : 'left-0'}
          left={
            <MassagerCsvFooter
              outputOption1={outputOption1}
              onOutputOption1Change={setOutputOption1}
              onExportCsv={handleExportCsv}
              isExporting={isExporting}
            />
          }
        />
      )}
    </div>
  );
};

export default MassagerCsvPage;
